// Kaarten slepen op het bord (zoals in het prototype): een kopie volgt de aanwijzer, de kolom eronder licht op, en
// aan de rand schuift het bord mee. Loslaten geeft de doelkolom en, bij groeperen per bucket, de groep eronder.
// Zonder beweging is het een gewone klik.
import { useRef, type PointerEvent } from "react";

export interface DropTarget { colKey: string | null; groupLabel: string | null }

export function useCardDrag(onDrop: (taskId: string, fromKey: string, to: DropTarget) => void) {
  const st = useRef<{ id: string; el: HTMLElement; x0: number; y0: number; moved: boolean; ghost: HTMLElement | null; from: string; auto: number; v: number } | null>(null);
  const suppressClick = useRef(false);

  const colAt = (x: number, y: number): DropTarget & { el: HTMLElement | null } => {
    const cols = Array.from(document.querySelectorAll<HTMLElement>(".p-col"));
    const el = cols.find((c) => { const r = c.getBoundingClientRect(); return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom; }) || null;
    let groupLabel: string | null = null;
    if (el) {
      const under = document.elementFromPoint(x, y) as HTMLElement | null;
      // een taak in een fasekaart: eerst naar de buitenste fasekaart, dan terug naar de groepskop ervoor
      let g = under?.closest<HTMLElement>(".p-task, .p-grp, .p-fk") || null;
      while (g?.parentElement?.closest(".p-fk")) g = g.parentElement.closest<HTMLElement>(".p-fk");
      while (g && !g.classList.contains("p-grp")) g = g.previousElementSibling as HTMLElement | null;
      groupLabel = g?.dataset.label || null;
    }
    return { el, colKey: el?.dataset.col || null, groupLabel };
  };
  const stopAuto = () => { const d = st.current; if (d?.auto) { window.clearInterval(d.auto); d.auto = 0; } };
  const cleanup = () => {
    const d = st.current;
    stopAuto();
    d?.ghost?.remove();
    d?.el.classList.remove("dragging");
    document.querySelectorAll(".p-col.drop").forEach((c) => c.classList.remove("drop"));
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", cancel);
  };
  function move(e: globalThis.PointerEvent) {
    const d = st.current; if (!d) return;
    const dx = e.clientX - d.x0, dy = e.clientY - d.y0;
    if (!d.moved) {
      if (Math.abs(dx) + Math.abs(dy) < 8) return;
      d.moved = true;
      const r = d.el.getBoundingClientRect(), g = d.el.cloneNode(true) as HTMLElement;
      g.classList.add("p-task-ghost");
      Object.assign(g.style, { left: r.left + "px", top: r.top + "px", width: r.width + "px" });
      document.body.appendChild(g); d.ghost = g; d.el.classList.add("dragging");
    }
    d.ghost!.style.transform = `translate(${dx}px, ${dy}px) rotate(1.5deg)`;
    const over = colAt(e.clientX, e.clientY);
    document.querySelectorAll<HTMLElement>(".p-col").forEach((c) => c.classList.toggle("drop", c === over.el && c.dataset.col !== d.from));
    // aan de rand van het bord schuift het mee
    const sc = document.querySelector<HTMLElement>(".p-cols");
    if (sc) {
      const r = sc.getBoundingClientRect();
      d.v = e.clientX > r.right - 56 ? 14 : e.clientX < r.left + 56 ? -14 : 0;
      if (d.v && !d.auto) d.auto = window.setInterval(() => { sc.scrollLeft += st.current?.v || 0; }, 16);
      if (!d.v) stopAuto();
    }
  }
  function up(e: globalThis.PointerEvent) {
    const d = st.current; if (!d) return;
    const over = colAt(e.clientX, e.clientY);
    cleanup();
    st.current = null;
    if (!d.moved) return;
    suppressClick.current = true;
    window.setTimeout(() => { suppressClick.current = false; }, 300);
    onDrop(d.id, d.from, over);
  }
  function cancel() { cleanup(); st.current = null; }

  const start = (taskId: string, fromKey: string) => (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    st.current = { id: taskId, el: e.currentTarget, x0: e.clientX, y0: e.clientY, moved: false, ghost: null, from: fromKey, auto: 0, v: 0 };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  };
  return { start, suppressClick };
}
